import { TokenWithValue } from '@/domain/common/types'
import { useConditionalFreeze } from '@/domain/hooks/useConditionalFreeze'
import { useMarketInfo } from '@/domain/market-info/useMarketInfo'
import { Token } from '@/domain/types/Token'
import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useChainId, usePublicClient, useWalletClient } from 'wagmi'
import { PageState, PageStatus } from '@/features/dialogs/common/types'
import { getFormFieldsForMintForm, useDebouncedMintFormValues } from './form'
import { FormFieldsForDialog, GsmMintState } from './types'
import { getGSMReserves } from './utils'
import { getMintFormValidator, MintFormSchema } from './validators'
import { assert } from '@/utils/assert'
import { useMarketWalletInfo } from '@/domain/wallet/useMarketWalletInfo'
import { useAccount } from '@/domain/hooks/useAccount'
import { usePrivy } from '@privy-io/react-auth'
import { gsmMint } from './gsmMint'
export interface UseMintFormOptions {
  initialToken: Token
}
export interface UseGsmMintFormResult {
  assetsToMintFields: FormFieldsForDialog
  fromAsset: TokenWithValue
  toAsset: TokenWithValue
  pageStatus: PageStatus
  form: UseFormReturn<MintFormSchema>
  guestMode: boolean
  gsmMintState: GsmMintState
  actionsEnabled: boolean
  openConnectModal: () => void
  gsmMint: () => void
  resetForm: () => void
}

export function useGsmMintForm(): UseGsmMintFormResult {
  const chainId = useChainId()

  const { connectWallet } = usePrivy()
  const { marketInfo } = useMarketInfo({ chainId, refetchInterval: 0 })
  const [pageStatus, setPageStatus] = useState<PageState>('form')
  const walletInfo = useMarketWalletInfo({ chainId })

  const [gsmMintState, setGsmMintState] = useState<GsmMintState>({
    message: '',
    status: 'idle',
  })

  const account = useAccount()
  const { data: walletClient } = useWalletClient()
  const publicClient = usePublicClient()

  const walletConfig = {
    account,
    publicClient,
    walletClient,
  }

  const { fromAssets, toAssets } = getGSMReserves(marketInfo, walletInfo)

  assert(fromAssets[0] && toAssets[0], 'No GSM tokens found')

  const form = useForm<MintFormSchema>({
    resolver: zodResolver(getMintFormValidator({ marketInfo, walletInfo }), undefined, { raw: true }),
    defaultValues: {
      fromAsset: {
        symbol: fromAssets[0].token.symbol,
        value: '',
      },
      toAsset: {
        symbol: toAssets[0].token.symbol,
        value: '',
      },
    },
    mode: 'onChange',
  })

  const { debouncedFormValues, isDebouncing, isFormValid } = useDebouncedMintFormValues({
    form,
    marketInfo,
  })

  const fromAsset = useConditionalFreeze(debouncedFormValues.fromAsset, pageStatus === 'success')
  const toAsset = useConditionalFreeze(debouncedFormValues.toAsset, pageStatus === 'success')
  const assetsToMintFields = getFormFieldsForMintForm({ form, marketInfo, fromAssets, toAssets, walletInfo })

  const actionsEnabled =
    debouncedFormValues.fromAsset.value.gt(0) &&
    debouncedFormValues.toAsset.value.gt(0) &&
    isFormValid &&
    !isDebouncing &&
    pageStatus === 'form'

  const handleGsmMint = async () => {
    return gsmMint({ setGsmMintState, fromAsset, walletConfig, chainId, onSuccess: () => setPageStatus('success') })
  }

  return {
    form,
    assetsToMintFields,
    fromAsset,
    toAsset,
    guestMode: !account,
    gsmMintState,
    actionsEnabled,
    gsmMint: handleGsmMint,
    openConnectModal: () => connectWallet(),
    resetForm: () => {
      form.reset()
      setPageStatus('form')
    },
    pageStatus: {
      actionsEnabled,
      state: pageStatus,
      goToSuccessScreen: () => setPageStatus('success'),
    },
  }
}
